import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowRightIcon,
  ChevronDownIcon,
  DownloadIcon,
} from "@heroicons/react/outline";
import AnchorChip from "@/components/lib/AnchorChip";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import Button from "@/components/actions/Button";
import { Menu } from "@headlessui/react";
import * as Text from "@/components/text";
import "swiper/css";
import "swiper/css/pagination";
import VideoTestimonial from "pages/iniciativas/VideoTestimonial";
import { localeTexts } from "@/utils/locale";
import { getStrapiImage } from "@/utils/strapiFunctions";

export default function Editions({ data, locale, title, subtitle }) {
  const editionsArray = data || [];
  const [selectedEdition, setSelectedEdition] = useState(null);

  useEffect(() => {
    if (editionsArray.length > 0) {
      setSelectedEdition(editionsArray[0]);
    }
  }, [data]);

  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [activeSlideIndex2, setActiveSlideIndex2] = useState(0);

  const getBulletWidthClass = (count) => {
    if (count > 18) return "w-2";
    if (count > 14) return "w-4";
    return "w-6";
  };

  if (editionsArray.length === 0) {
    return null;
  }

  return (
    <div className="w-full pt-10 sm:pb-[32px] lg:pb-[50px]">
      <div className="w-full font-light leading-[42px] tracking-[-0.8px] sm:text-center sm:text-[24px] md:text-[32px] lg:text-left lg:text-[40px]">
        {title}
      </div>
      <div className="w-full font-light leading-[42px] tracking-[-0.8px] sm:text-center sm:text-[18px] md:text-[24px] lg:text-left lg:text-[32px]">
        {subtitle}
      </div>

      <div className="my-5 flex gap-2 overflow-scroll">
        {editionsArray.map((edition) => (
          <AnchorChip
            key={edition.id}
            active={selectedEdition?.id === edition.id}
            onClick={() => setSelectedEdition(edition)}
          >
            {edition.short_title}
          </AnchorChip>
        ))}
      </div>

      {selectedEdition?.id ? (
        <div className="mx-auto flex max-w-[974px] sm:w-[328px] sm:flex-col md:w-full md:flex-row">
          <div className="flex w-full flex-col bg-white sm:w-[328px] sm:text-center md:w-full md:text-left lg:h-full lg:w-full">
            <span className="w-full font-bold leading-10 sm:text-[28px] md:text-[32px] lg:pb-4 lg:text-4xl">
              {selectedEdition?.title}
            </span>
            <span className="font-bold sm:text-[16px] sm:leading-8 md:text-xl md:leading-10 lg:pb-3 lg:text-2xl">
              {selectedEdition.subtitle} | {selectedEdition.header}&nbsp;
            </span>
            <span className="leading-6 sm:mb-8 sm:text-base md:mb-10 lg:mb-8 lg:text-lg">
              {selectedEdition.description}
            </span>
            <div className="-mt-10 flex sm:flex-col sm:items-center md:flex-row md:items-start lg:flex-row">
              {selectedEdition?.download_urls?.length > 0 ? (
                <Menu
                  as="div"
                  className="relative w-[225px] sm:mt-5 lg:mr-6 lg:mt-10"
                >
                  <Menu.Button
                    as={Button}
                    color="secondary-light"
                    className="h-[52px] w-full border border-neutral-900"
                    label={localeTexts[locale].initiatives.vivero.convocatoria}
                    trailingIcon={<ChevronDownIcon width={32} height={32} />}
                  />
                  <Menu.Items
                    as="div"
                    className="absolute z-10 w-full bg-white"
                  >
                    {selectedEdition?.download_urls.map((option, i) => (
                      <Menu.Item key={i}>
                        {({ active }) => (
                          <Link href={option?.download_url} target="_blank">
                            <div
                              className={`flex cursor-pointer items-center border border-t-0 border-gray-500 p-3 active:bg-black active:text-white ${
                                active ? "bg-neutral-50" : ""
                              }`}
                            >
                              <div className="group grow">
                                <Text.Label
                                  size="sm"
                                  as="p"
                                  className="text-neutral-1000 group-active:text-white"
                                >
                                  {option.name}
                                </Text.Label>
                              </div>
                              <div className="text-neutral-800 group-active:text-white/60">
                                <DownloadIcon className="h-5 w-5" />
                              </div>
                            </div>
                          </Link>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Menu>
              ) : null}
              {selectedEdition?.link && (
                <Link
                  key={selectedEdition?.link}
                  href={selectedEdition?.link}
                  passHref
                >
                  <Button
                    color="terciary-light"
                    size="lg"
                    className="w-[225px] sm:mt-5 md:ml-4 lg:mr-6 lg:mt-10"
                    trailingIcon={<ArrowRightIcon />}
                    label={localeTexts[locale].initiatives.vivero.catalogue}
                  />
                </Link>
              )}
            </div>
            <div id="poster" className="my-8 grid gap-4 md:grid-cols-12">
              {getStrapiImage(selectedEdition?.preview_image, "medium") && (
                <div className="md:col-span-4">
                  <Image
                    width={360}
                    height={470}
                    src={getStrapiImage(selectedEdition?.preview_image, "medium")}
                    className="h-96 w-80"
                    alt={selectedEdition.id}
                  />
                </div>
              )}
              <div
                id="video"
                className="h-96 sm:max-w-[328px] md:col-span-8 md:max-w-none"
              >
                {selectedEdition.video_url ? (
                  <VideoTestimonial
                    key={selectedEdition.video_url}
                    urlVideo={selectedEdition?.video_url}
                  />
                ) : null}
              </div>

              <div
                key="momentos"
                className="relative h-full pt-4 sm:max-w-[328px] md:col-span-6 md:max-w-none"
              >
                {selectedEdition.media && selectedEdition.media.length > 0 ? (
                  <Swiper
                    modules={[Pagination]}
                    spaceBetween={10}
                    slidesPerView={"auto"}
                    pagination={{
                      el: "#paginator1",
                      clickable: true,
                      type: "bullets",
                      currentClass: "bg-neutral-900",
                      bulletClass: `inline-block ${getBulletWidthClass(
                        selectedEdition.media?.length
                      )} h-1 bg-gray-200 mx-1`,
                      bulletActiveClass: "bg-neutral-900",
                    }}
                    onSlideChange={(swiper) =>
                      setActiveSlideIndex(swiper.activeIndex)
                    }
                  >
                    {selectedEdition.media.map((item, index) => (
                      <SwiperSlide key={index}>
                        <h1 className="truncate font-bold sm:text-[16px] sm:leading-8 md:text-xl md:leading-10 lg:pb-3 lg:text-2xl">
                          {item.title}
                        </h1>
                        <div className="relative h-80 w-full overflow-hidden">
                          <div className="h-0 w-full pb-[56.25%]">
                            <Image
                              src={getStrapiImage(item?.image, "medium")}
                              alt={`Media ${index}`}
                              layout="fill"
                              objectFit="cover"
                            />
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                    <div>
                      <div id="paginator1" className="cursor-pointer text-center"></div>
                    </div>
                    {activeSlideIndex !== null &&
                      selectedEdition.media[activeSlideIndex] && (
                        <p className="text-wrap text-base">
                          {selectedEdition.media[activeSlideIndex].description}
                        </p>
                      )}
                  </Swiper>
                ) : null}
              </div>

              <div
                key="invitades"
                className="relative h-full pt-4 sm:max-w-[328px] md:col-span-6 md:max-w-none"
              >
                {selectedEdition.media2 && selectedEdition.media2.length > 0 ? (
                  <Swiper
                    modules={[Pagination]}
                    spaceBetween={10}
                    slidesPerView={"auto"}
                    pagination={{
                      el: "#paginator2",
                      clickable: true,
                      type: "bullets",
                      currentClass: "bg-neutral-900",
                      bulletClass: `inline-block ${getBulletWidthClass(
                        selectedEdition.media2?.length
                      )} h-1 bg-gray-200 mx-1`,
                      bulletActiveClass: "bg-neutral-900",
                    }}
                    onSlideChange={(swiper) =>
                      setActiveSlideIndex2(swiper.activeIndex)
                    }
                  >
                    {selectedEdition.media2.map((item, index) => (
                      <SwiperSlide key={index}>
                        <div className="h-fit">
                          <h1 className="truncate font-bold sm:text-[16px] sm:leading-8 md:text-xl md:leading-10 lg:pb-3 lg:text-2xl">
                            {item.title}
                          </h1>
                          <div className="relative flex h-80 w-full overflow-hidden">
                            <div className="h-0 w-full">
                              <Image
                                src={getStrapiImage(item?.image, "medium")}
                                alt={`Media ${index}`}
                                layout="fill"
                                objectFit="cover"
                              />
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                    <div>
                      <div
                        id="paginator2"
                        className="cursor-pointer text-center"
                      ></div>
                    </div>
                    {activeSlideIndex2 !== null &&
                      selectedEdition.media2[activeSlideIndex2] && (
                        <p className="text-wrap text-base">
                          {
                            selectedEdition.media2[activeSlideIndex2]
                              .description
                          }
                        </p>
                      )}
                  </Swiper>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}